/* ==========================================================================
   CUSTOM — Liberator / Kite cross-through pricing on native theme prices
   DATE: 2026-07-11
   PURPOSE:
     Kite's app-embed only injects its strikethrough price
     (.gfg-collection-strikethrough-wrapper) into product cards on COLLECTION
     pages. Everywhere else the theme's own price renders undiscounted — the
     home page (e.g. the "Icons of Desire" products-with-image section), the
     cards on a product page (related / recently viewed), the cart-drawer
     cross-sell, a featured-product or hot-spot section, and so on.

     This script fills that gap. It reuses the Kite rule engine that already
     lives in layout/theme.liquid (getKiteDiscountPercentForProductHandle),
     and renders the exact same markup + inline styles Kite uses, so the result
     is visually identical to the collection page.

   SCOPE:
     Every <price-list> on the page that can be tied to a product, EXCEPT:
       - collection & search templates  → Kite handles those itself
       - the PDP main price             → Kite's own app block handles it
       - the sticky add-to-cart bar     → synced from Kite by another script
       - the ENTIRE cart drawer & cart  → must keep its current behaviour: no
         page (incl. cross-sell)          cross-through until the customer
                                          enters the discount code
       - the quick-buy modal            → price changes with variant selection,
                                          a static injection would go stale
     Anything else with a resolvable product handle gets the cross-through.

   HOW TO REVERT:
     Delete the <script> tag that loads this file from layout/theme.liquid.
     This file can remain in assets without any effect.
   ========================================================================== */
(function () {
  'use strict';

  var PAGE_TYPE = (window.ShopifyAnalytics &&
                   window.ShopifyAnalytics.meta &&
                   window.ShopifyAnalytics.meta.page &&
                   window.ShopifyAnalytics.meta.page.pageType) || '';

  // Kite already handles cards on these templates — do not touch them.
  if (PAGE_TYPE === 'collection' || PAGE_TYPE === 'search') return;

  var PROCESSED_ATTR = 'data-kite-card-priced';

  /* Kite styles its collection-page spans with INLINE styles (from the colour
     settings in the Kite admin), not with a CSS class — so the line-through
     lives on the element itself. We mirror those inline styles here. If you
     change the strikethrough colours in Kite, update these to match. */
  var STYLE_ORIGINAL   = 'text-decoration: line-through; color: #999999;';
  var STYLE_DISCOUNTED = 'color: #333333;';
  var STYLE_SAVINGS    = 'color: #52572e;';

  var money = function (value) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(value);
    } catch (e) {
      return '$' + value.toFixed(2);
    }
  };

  var kiteReady = function () {
    return typeof window.getKiteDiscountPercentForProductHandle === 'function' &&
      !!((window.gfg && window.gfg.settings && window.gfg.settings.consolidatedCustomDiscount) ||
         (window.kite_app_data && window.kite_app_data.settings &&
          window.kite_app_data.settings.consolidatedCustomDiscount));
  };

  /* Surfaces whose price is already handled elsewhere — never touch these.

     NOTE ON THE CART: the entire cart drawer and cart page are excluded on
     purpose. Cart pricing must keep its existing behaviour — no cross-through
     until the customer actually enters the discount code. That is driven by
     the separate cart scripts in theme.liquid; this script must not pre-empt
     it, including on the cart drawer's cross-sell recommendations. */
  var EXCLUDED = [
    'cart-drawer',                   // whole cart drawer, incl. cross-sell
    '.shopify-section--cart-drawer',
    'main-cart',                     // cart page
    '.cart-drawer__items',
    'cart-items',
    'line-item',
    '.line-item',
    'product-sticky-add-to-cart',    // synced from Kite by another script
    'quick-buy-modal'                // price changes with variant selection
  ].join(', ');

  var handleFromHref = function (el) {
    var href = el ? el.getAttribute('href') || '' : '';
    var match = href.match(/\/products\/([^?#]+)/);
    return match ? match[1] : '';
  };

  /* Resolve the product a <price-list> belongs to. Cards expose handle="…";
     everything else (cross-sell, featured product, hot spots) is resolved by
     walking up until we find a container holding a link to the product. */
  var getHandle = function (priceList) {
    var card = priceList.closest('product-card');
    if (card && card.getAttribute('handle')) return card.getAttribute('handle');

    var tagged = priceList.closest('[data-product-handle]');
    if (tagged) return tagged.getAttribute('data-product-handle');

    var node = priceList.parentElement;
    for (var depth = 0; node && depth < 6; depth++) {
      var handle = handleFromHref(node.querySelector('a[href*="/products/"]'));
      if (handle) return handle;
      node = node.parentElement;
    }

    return '';
  };

  var getOriginalPrice = function (salePriceEl) {
    var raw = (salePriceEl.innerText || '').replace(/sale price/i, '');
    var match = raw.match(/[\d,]+(\.\d+)?/);
    if (!match) return NaN;
    return parseFloat(match[0].replace(/,/g, ''));
  };

  var applyKitePricingToCards = async function () {
    if (!kiteReady()) return;

    var priceLists = document.querySelectorAll('price-list');

    for (var i = 0; i < priceLists.length; i++) {
      var priceList = priceLists[i];

      // Already done by us, or already done by Kite itself.
      if (priceList.hasAttribute(PROCESSED_ATTR)) continue;
      if (priceList.querySelector('.gfg-collection-strikethrough-wrapper')) continue;

      // Surfaces owned by Kite or by the other custom scripts.
      if (priceList.closest(EXCLUDED)) continue;
      if (PAGE_TYPE === 'product' && priceList.closest('product-info')) continue;
      if (priceList.parentElement &&
          priceList.parentElement.querySelector('.gfg-strikethrough-container')) continue;

      var salePriceEl = priceList.querySelector('sale-price');
      if (!salePriceEl) continue;

      var handle = getHandle(priceList);
      if (!handle) continue;

      var originalPrice = getOriginalPrice(salePriceEl);
      if (isNaN(originalPrice) || originalPrice <= 0) continue;

      // Mark before awaiting so a re-run can't process the same price twice.
      priceList.setAttribute(PROCESSED_ATTR, 'pending');

      var percent = 0;
      try {
        percent = await window.getKiteDiscountPercentForProductHandle(handle);
      } catch (e) {
        percent = 0;
      }

      if (!percent) {
        priceList.setAttribute(PROCESSED_ATTR, 'none');
        continue;
      }

      // Card may have been recycled (carousel) while we awaited.
      if (getHandle(priceList) !== handle) {
        priceList.removeAttribute(PROCESSED_ATTR);
        continue;
      }
      if (priceList.querySelector('.gfg-collection-strikethrough-wrapper')) {
        priceList.setAttribute(PROCESSED_ATTR, 'kite');
        continue;
      }

      var discounted = originalPrice * (1 - percent / 100);
      var savings = originalPrice - discounted;

      // Hide the theme's own prices, exactly as Kite does on collection pages.
      salePriceEl.style.display = 'none';
      var compareAtEl = priceList.querySelector('compare-at-price');
      if (compareAtEl) compareAtEl.style.display = 'none';

      var wrapper = document.createElement('div');
      wrapper.className = 'gfg-collection-strikethrough-wrapper';
      wrapper.setAttribute('data-product-handle', handle);
      wrapper.innerHTML =
        '<span class="gfg-original-price" style="' + STYLE_ORIGINAL + '">' + money(originalPrice) + '</span> ' +
        '<span class="gfg-discounted-price gfg-primary-text" style="' + STYLE_DISCOUNTED + '">' + money(discounted) + '</span> ' +
        '<span class="gfg-savings gfg-secondary-text" style="' + STYLE_SAVINGS + '">(Save ' + money(savings) + ' - ' + percent + '%)</span>';

      priceList.appendChild(wrapper);
      priceList.setAttribute(PROCESSED_ATTR, 'applied');
    }
  };

  var timer = null;
  var schedule = function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      applyKitePricingToCards();
    }, 250);
  };

  // Kite's app-embed loads async — wait for its data before the first pass.
  var attempts = 0;
  var waitForKite = setInterval(function () {
    attempts++;
    if (kiteReady()) {
      clearInterval(waitForKite);
      schedule();
      observe();
    } else if (attempts > 60) { // ~15s
      clearInterval(waitForKite);
    }
  }, 250);

  // Re-run when cards are injected later (carousels, quick buy, section re-render).
  function observe() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches('price-list, product-card')) ||
              (node.querySelector && node.querySelector('price-list'))) {
            schedule();
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }
})();